// Ported from dev-scrips-pm-react src/scheduling/components/shared/provider-picker-grid.tsx (PROD-1190 calendar port)
import { useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { Avatar, Icon } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { QueryErrorState } from '@/shared/components/query-error-state';
import { specialityDisplay } from '@/scheduling/lib/calendar-mapping';
import type { ProviderSlotsResponse } from '@/services/scheduling/use-provider-slots';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

/**
 * ProviderPickerGrid — shared, presentational 2-column provider
 * picker used by both:
 *   - the create-appointment panel (`provider-section.tsx`)
 *   - the block-availability panel (`block-availability-provider-search.tsx`)
 *
 * Behavior matches the appointment panel's original inline picker
 * (the canonical reference): collapsed at 4 cards with a chevron
 * toggle, selected provider replaces the grid with a brand-primary
 * card showing the × clear button. Search filtering is the caller's
 * responsibility — pass the already-filtered `providers` list so
 * each consumer owns its server-vs-client filter strategy.
 *
 * Reuse rationale: previously the two panels diverged subtly (block
 * panel rendered a single-column list, missed the chevron toggle).
 * This component is the single source of truth for the visual
 * pattern so any future polish (skeleton tweaks, hover state, ARIA)
 * lands in one place.
 */

function getInitials(name: string): string {
  const parts = name.replace(/^Dr\.?\s*/i, '').split(' ').filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase() || '?';
}

function formatWorkingHours(hours: { startTime?: string; endTime?: string }[] | undefined): string {
  if (!hours || hours.length === 0) return 'Non working';
  const compact = (t?: string) => (t ? t.toLowerCase().slice(0, -1).replace(/\s+/g, '') : '');
  const start = compact(hours[0].startTime);
  const end = compact(hours[0].endTime);
  if (!start || !end) return 'Non working';
  return `${start} - ${end}`;
}

/**
 * Minimal projection of `ProviderSlotsResponse` for the SELECTED state.
 * The block panel and appointment panel both have different "selected"
 * shapes (BlockPractitioner vs RHF form values), so the picker takes
 * the small subset it needs.
 */
export interface SelectedProviderInfo {
  id: string;
  name: string;
  photo?: string | null;
  speciality?: string | null;
}

interface Props {
  /** The full provider list to render — caller is responsible for
   *  any filtering / search narrowing before passing in. */
  providers: ProviderSlotsResponse[];
  /** Currently-selected provider (replaces the grid). */
  selected: SelectedProviderInfo | null;
  isLoading: boolean;
  onSelect: (provider: ProviderSlotsResponse) => void;
  onClear: () => void;
  /** Bound search input (controlled). Pass `null` to hide the input
   *  (e.g. when the consumer wants its own search affordance). */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Hide the search input entirely. Default false. */
  hideSearch?: boolean;
  /** Number of cards shown before the chevron clamps the list.
   *  Default 4 (matches the appointment panel's COLLAPSED_COUNT). */
  collapsedCount?: number;
  /** Override the empty / no-match placeholder text. */
  emptyText?: string;
  /** Override the searching-with-no-results placeholder text. */
  noMatchText?: string;
  /** The provider query FAILED (5xx/network) — render the error state, never `emptyText`
   *  (PROD-1657: a masked ProviderSlots 500 read as "no providers available"). */
  error?: boolean;
  /** Retry affordance for the error state — pass the query's `refetch`. */
  onRetry?: () => void;
  /**
   * View-only mode. Hides the × clear button on the selected card,
   * the search input, and disables provider-card buttons. Used by
   * the block-availability panel in edit mode (PR 5) where the user
   * can only see which provider the existing block belongs to.
   */
  readOnly?: boolean;
  /**
   * `list` draws the booking design's provider list (SchedBookingForm `.cards` / `.pcard`): one column of person cards,
   * the right slot reading "N today" when the response carries `totalSlots`, else the working hours; the chosen one a
   * blue card with the clear button beside it. `grid` (default) is the two-column picker (block availability).
   */
  layout?: 'grid' | 'list';
}

export function ProviderPickerGrid({
  providers,
  selected,
  isLoading,
  onSelect,
  onClear,
  searchValue = '',
  onSearchChange,
  hideSearch = false,
  collapsedCount = 4,
  emptyText = 'No providers available for this date.',
  noMatchText = 'No providers match this search.',
  error = false,
  onRetry,
  readOnly = false,
  layout = 'grid',
}: Props) {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleProviders = isExpanded ? providers : providers.slice(0, collapsedCount);
  const canExpand = providers.length > collapsedCount;

  const list = layout === 'list';

  if (selected && list) {
    return (
      <div className="booking-provider-chosen">
        <div className="booking-pcard" data-selected="">
          <Avatar
            size="md"
            className="flex-shrink-0"
            src={selected.photo ?? undefined}
            name={selected.name}
            initials={getInitials(selected.name)}
            alt=""
          />
          <span className="booking-pcard-text">
            <span className="booking-pcard-name">{selected.name.replace(/^Dr\.?\s*/i, 'Dr. ')}</span>
            {selected.speciality && <span className="booking-pcard-sub">{selected.speciality}</span>}
          </span>
        </div>
        {!readOnly && (
          <button
            type="button"
            aria-label="Clear selected provider"
            onClick={onClear}
            className="booking-icon-btn"
            data-tone="error"
          >
            <Icon name="close" size={16} aria-hidden />
          </button>
        )}
      </div>
    );
  }

  if (selected) {
    return (
      <div className="pgrid-chosen">
        <Avatar
          size="sm"
          className="flex-shrink-0"
          src={selected.photo ?? undefined}
          name={selected.name}
          initials={getInitials(selected.name)}
          alt=""
        />
        <div className="pgrid-grow">
          <p className="pgrid-name">
            {selected.name.replace(/^Dr\.?\s*/i, 'Dr. ')}
          </p>
          {selected.speciality && (
            <p className="pgrid-sub">{selected.speciality}</p>
          )}
        </div>
        {!readOnly && (
        <button
          type="button"
          aria-label="Clear selected provider"
          onClick={onClear}
          className="pgrid-clear"
        >
          <X className="size-4" />
        </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!hideSearch && (
        <div className="pgrid-search-wrap">
          <Search className="pgrid-search-icon" />
          <input
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder="Search provider by name or speciality"
            className="pgrid-search"
          />
        </div>
      )}

      {isLoading ? (
        <div className={list ? 'booking-pcards' : 'pgrid-cards'}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={list ? 'booking-pcard-skeleton' : 'pgrid-skel'} />
          ))}
        </div>
      ) : error ? (
        // ERROR before EMPTY (PROD-1657): a failed provider search must never read as
        // "no providers available" — outage and true zero are different clinical facts.
        <QueryErrorState message="Couldn't load providers — please retry." onRetry={onRetry} className="py-2" />
      ) : providers.length === 0 ? (
        <p className={list ? 'booking-empty' : 'pgrid-muted'}>
          {searchValue ? noMatchText : emptyText}
        </p>
      ) : (
        <>
          <div className={list ? 'booking-pcards' : 'pgrid-cards'}>
            {visibleProviders.map((p, i) => {
              if (list) {
                const speciality = specialityDisplay(p.speciality);
                return (
                  <button key={p.id || `provider-${i}`} type="button" onClick={() => onSelect(p)} className="booking-pcard">
                    <Avatar
                      size="md"
                      className="flex-shrink-0"
                      src={p.photo ?? undefined}
                      name={p.name}
                      initials={getInitials(p.name)}
                    />
                    <span className="booking-pcard-text">
                      <span className="booking-pcard-name">{(p.name ?? 'Unknown').replace(/^Dr\.?\s*/i, 'Dr. ')}</span>
                      {speciality && <span className="booking-pcard-sub">{speciality}</span>}
                    </span>
                    <span className="booking-pcard-count">
                      {typeof p.totalSlots === 'number' ? `${p.totalSlots} today` : formatWorkingHours(p.workingHours)}
                    </span>
                  </button>
                );
              }
              const speciality = specialityDisplay(p.speciality);
              const workingHours = formatWorkingHours(p.workingHours);
              return (
                <button
                  key={p.id || `provider-${i}`}
                  type="button"
                  onClick={() => onSelect(p)}
                  className="pgrid-card"
                >
                  <Avatar
                    size="md"
                    className="flex-shrink-0"
                    src={p.photo ?? undefined}
                    name={p.name}
                    initials={getInitials(p.name)}
                  />
                  <div className="pgrid-grow">
                    <p className="pgrid-card-name">
                      {(p.name ?? 'Unknown').replace(/^Dr\.?\s*/i, 'Dr. ')}
                    </p>
                    {speciality && (
                      <p className="pgrid-card-sub">
                        {speciality}
                      </p>
                    )}
                    <p className="pgrid-card-sub">
                      {workingHours}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {canExpand && (
            <button
              type="button"
              onClick={() => setIsExpanded((v) => !v)}
              className="icon-action mt-2"
              aria-label={isExpanded ? 'Show less' : 'Show more providers'}
              aria-expanded={isExpanded}
            >
              <ChevronDown
                className={cn('h-5 w-5 transition-transform', isExpanded && 'rotate-180')}
              />
            </button>
          )}
        </>
      )}
    </div>
  );
}
